import React, { useEffect, useState } from "react";
import {
  Form,
  Switch,
  Typography,
  Divider,
  Space,
  Button,
  Row,
  Col,
  message,
} from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  BellOutlined,
  MailFilled,
  MessageOutlined,
} from "@ant-design/icons";
import apiRequest from "src/common/utils/apiRequest";
import LoadingPreview from "src/common/components/LoadingPreview";
import coreFunctions from "src/common/utils/coreFunctions";

const { Text, Title } = Typography;

const NotificationPreferences: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const onFinish = async () => {
    setSaving(true);
    try {
      await form.validateFields();

      const API_REQUEST = new apiRequest(
        "user/profile/setting/notification/edit",
        true
      );

      await API_REQUEST.send(
        form.getFieldsValue(),
        (response: { status: string; message: string }) => {
          if (response.status === "success") {
            message.success(response.message);
          } else {
            message.error(response.message);
          }
        }
      );
    } catch (errorInfo: any) {
      message.error(errorInfo.errorFields[0].errors[0]);
    }
    setSaving(false);
  };

  useEffect(() => {
    const getData = () => {
      setLoading(true);
      const API_REQUEST = new apiRequest(
        "user/profile/setting/notification/get",
        true
      );

      API_REQUEST.send(
        {},
        (response: { status: string; data: any; message: string }) => {
          if (response.status === "success") {
            form.setFieldsValue(response.data);
            setLoading(false);
          } else {
            message.error(response.message);
          }
        }
      );
    };

    getData();
  }, []);

  return (
    <div>
      <Title level={3} style={{ marginTop: "0" }}>
        Notification Preferences
      </Title>
      <Text type="secondary">Manage your notification preferences</Text>

      <Divider />
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {loading ? (
          <LoadingPreview />
        ) : (
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Row
              style={{
                textAlign: coreFunctions.isMobileDevice() ? "left" : "center",
              }}
            >
              <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                <Text strong>
                  <MailOutlined style={{ marginRight: 8 }} />
                  Email Notifications
                </Text>
                <Form.Item name="allow_email_notify" valuePropName="checked">
                  <Switch checkedChildren="On" unCheckedChildren="Off" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                <Text strong>
                  <MessageOutlined style={{ marginRight: 8 }} />
                  Phone
                </Text>
                <Form.Item name="allow_sms_notify" valuePropName="checked">
                  <Switch checkedChildren="On" unCheckedChildren="Off" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                <Text strong>
                  <BellOutlined style={{ marginRight: 8 }} />
                  Push Notifications
                </Text>
                <Form.Item name="allow_push_notify" valuePropName="checked">
                  <Switch checkedChildren="On" unCheckedChildren="Off" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Email Notifications */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>
                <MailOutlined style={{ marginRight: 8 }} />
                Email Notifications
              </Text>
              <div style={{ marginTop: 8 }}>
                <Row gutter={[16, 0]}>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Projects</Text>}
                      name="project"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Files</Text>}
                      name="files"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Customers</Text>}
                      name="client"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Sales</Text>}
                      name="sales"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Accounting</Text>}
                      name="accounting"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">HR</Text>}
                      name="hr"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Timesheets & Leave</Text>}
                      name="timesheet_leave"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Expenses</Text>}
                      name="expenses"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Purchase</Text>}
                      name="purchase"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Inventory</Text>}
                      name="inventory"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Fleet Management</Text>}
                      name="fleet_mangment"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Requisitions</Text>}
                      name="requisitions"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Tasks</Text>}
                      name="tasks"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={12} md={8} lg={6} xl={4}>
                    <Form.Item
                      label={<Text type="secondary">Support</Text>}
                      name="support"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <Form.Item style={{ marginTop: 8 }}>
              <Button
                type="primary"
                loading={saving}
                htmlType="submit"
                style={{ width: 120 }}
              >
                Save Changes
              </Button>
            </Form.Item>
          </Form>
        )}
      </Space>
    </div>
  );
};

export default NotificationPreferences;
